import {
  dateToNetDate,
  toStringOrNull,
  toTrimmedString,
  isNullOrUndefined,
  makeInvitationLink,
  formatUnixTimestamp,
  isNullOrEmptyString,
  formatTwoDecimalPoints,
  isNotNullOrEmptyString,
  toTrimmedStringLowerCase,
  netDateToDateOrEmptyString,
  netDateToDateNicelyFormatted,
} from './misc'
import {
  HOST,
  DEBUG_MODE,
  IS_PRODUCTION,
  PROVISIONING_HARDWARE_PLACEHOLDER,
  STRIPE_SUBSCRIPTION_STATUS_ACTIVE,
  STRIPE_SUBSCRIPTION_STATUS_PAUSED,
  STRIPE_SUBSCRIPTION_STATUS_UNPAID,
  STRIPE_SUBSCRIPTION_STATUS_PAST_DUE,
  STRIPE_SUBSCRIPTION_STATUS_TRIALING,
  STRIPE_SUBSCRIPTION_STATUS_CANCELED,
  STRIPE_SUBSCRIPTION_INTERVAL_YEARLY,
  STRIPE_SUBSCRIPTION_INTERVAL_MONTHLY,
  STRIPE_SUBSCRIPTION_STATUS_INCOMPLETE,
  STRIPE_SUBSCRIPTION_STATUS_INCOMPLETE_EXPIRED,
} from './config'
import {
  isFrench,
  isKorean,
  isSpanish,
  translate,
  LANGUAGE_CODE_FRENCH,
  LANGUAGE_CODE_KOREAN,
  LANGUAGE_CODE_ENGLISH,
  LANGUAGE_CODE_SPANISH,
} from './language'
import { toastError } from './notification'

export const MODAL_MODE_SELECT_MODE: number = 0
export const MODAL_MODE_REDUCE_QUANTITY: number = 1
export const MODAL_MODE_DELETE_SUBSCRIPTION: number = 2
export const MODAL_MODE_ADD_ZONE: number = 3

export const MAXIMUM_USER_INTERNAL_NOTES_LENGTH: number = 1900

export const MAXIMUM_HARDWARE_SEATS: number = 5000
export const MAXIMUM_SOFTWARE_SEATS: number = 5000

export const MAXIMUM_BULK_INVITATION_TEXTAREA_CHARACTERS: number = 5000
export const MAXIMUM_BULK_INVITATION_EMAILS: number = 50

export const RESEND_SINGLE_INVITATION_ERROR_CODE_NODE: number = -1
export const RESEND_SINGLE_INVITATION_ERROR_CODE_UNSUBSCRIBED_EMAIL: number = 1
export const RESEND_SINGLE_INVITATION_ERROR_CODE_EXCEPTION: number = 2
export const RESEND_SINGLE_INVITATION_ERROR_CANT_RESEND_FOR_NOT_PREPARED_INVITATION: number = 3
export const RESEND_SINGLE_INVITATION_ERROR_CODE_INVITATION_NOT_FOUND: number = 4
export const RESEND_SINGLE_INVITATION_ERROR_CODE_TOO_SOON_TO_RESEND: number = 5
export const RESEND_SINGLE_INVITATION_MINUTES_BETWEEN_RESENDS: number = 1

export function getSubscriptionBundles(selectedLanguage: any) {
  return [
    {
      value: 10,
      label: translate(
        '+10 Zones',
        '+10 Zonas',
        '+10 Zones',
        '+10개 구역',
        selectedLanguage,
      ),
      sub: translate(
        'Local expansion',
        'Expansión local',
        'Expansion Locale',
        '지역 확장',
        selectedLanguage,
      ),
    },
    {
      value: 100,
      label: translate(
        '+100 Zones',
        '+100 Zonas',
        '+100 Zones',
        '+100개 구역',
        selectedLanguage,
      ),
      sub: translate(
        'Regional footprint',
        'Huella regional',
        'Empreinte Régionale',
        '광역 기반 구축',
        selectedLanguage,
      ),
    },
    {
      value: 1000,
      label: translate(
        '+1,000 Zones',
        '+1.000 Zonas',
        '+1,000 Zones',
        '+1,000개 구역',
        selectedLanguage,
      ),
      sub: translate(
        'State-wide scale',
        'Escala estatal',
        'Échelle Étatique',
        '주 단위 규모',
        selectedLanguage,
      ),
    },
    {
      value: 5000,
      label: translate(
        '+5,000 Zones',
        '+5.000 Zonas',
        '+5,000 Zones',
        '+5,000개 구역',
        selectedLanguage,
      ),
      sub: translate(
        'National rollout',
        'Despliegue nacional',
        'Déploiement National',
        '전국 출시',
        selectedLanguage,
      ),
    },
  ]
}

export function formatWeb2SessionId(web2UserId: any): string {
  try {
    return isNotNullOrEmptyString(web2UserId) &&
      web2UserId?.toString().trim().length > 8
      ? web2UserId?.toString().trim().substring(0, 3) +
          ' ... ' +
          web2UserId
            ?.toString()
            .trim()
            .substring(web2UserId?.toString().trim().length - 3)
      : toTrimmedString(web2UserId)
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
    return ''
  }
}

const MAX_IDENTIFIER_LEN = 24

const truncateMiddle = (
  str: any,
  maxLength: number = MAX_IDENTIFIER_LEN,
): string => {
  let ret: string = toTrimmedString(str)
  try {
    if (ret.length > maxLength) {
      const separator = '...'
      const charsToShow = maxLength - separator.length
      const frontChars = Math.ceil(charsToShow / 2)
      const backChars = Math.floor(charsToShow / 2)

      ret =
        ret.substring(0, frontChars) +
        separator +
        ret.substring(ret.length - backChars)
    }
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
  }
  return ret
}

export const formatDisplayName = (displayName: any): string => {
  const clean = toTrimmedString(displayName)
  return truncateMiddle(clean)
}

export const formatLastFirstName = (lastName: any, firstName: any): string => {
  const cleanLast = toTrimmedString(lastName)
  const cleanFirst = toTrimmedString(firstName)
  const combined = `${cleanLast}, ${cleanFirst}`
  return truncateMiddle(combined)
}

export function formatEmail(email: any): string {
  try {
    return isNotNullOrEmptyString(email) && email?.toString().trim().length > 8
      ? email?.toString().trim().substring(0, 3) +
          ' ... ' +
          email
            ?.toString()
            .trim()
            .substring(email?.toString().trim().length - 4)
      : toTrimmedString(email)
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
    return ''
  }
}

export function formatEmailSmall_Tooltip(email: any): string {
  try {
    return isNotNullOrEmptyString(email) && email?.toString().trim().length > 15
      ? toTrimmedString(email)
      : ''
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
    return ''
  }
}

export function formatEmailSmall(email: any): string {
  try {
    return isNotNullOrEmptyString(email) && email?.toString().trim().length > 15
      ? email?.toString().trim().substring(0, 7) +
          ' ... ' +
          email
            ?.toString()
            .trim()
            .substring(email?.toString().trim().length - 7)
      : toTrimmedString(email)
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
    return ''
  }
}

export function formatEmailMedium_Tooltip(email: any): string {
  try {
    return isNotNullOrEmptyString(email) && email?.toString().trim().length > 18
      ? toTrimmedString(email)
      : ''
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
    return ''
  }
}

const FORMAT_EMAIL_MEDIUM_THRESHOLD: number = 18
const FORMAT_EMAIL_LARGE_THRESHOLD: number = 28
const FORMAT_EMAIL_EXTRA_EXTRA_LARGE_THRESHOLD: number = 48
const FORMAT_EMAIL_CRAZY_LARGE_THRESHOLD: number = 256

export function formatEmailMedium(email: any): string {
  try {
    return isNotNullOrEmptyString(email) &&
      email?.toString().trim().length > FORMAT_EMAIL_MEDIUM_THRESHOLD
      ? email?.toString().trim().substring(0, 8) +
          ' ... ' +
          email
            ?.toString()
            .trim()
            .substring(email?.toString().trim().length - 9)
      : toTrimmedString(email)
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
    return ''
  }
}

export function formatEmailLarge_Tooltip(email: any): string {
  try {
    return isNotNullOrEmptyString(email) &&
      email?.toString().trim().length > FORMAT_EMAIL_LARGE_THRESHOLD
      ? toTrimmedString(email)
      : ''
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
    return ''
  }
}

export function formatEmailLarge(email: any): string {
  try {
    return isNotNullOrEmptyString(email) &&
      email?.toString().trim().length > FORMAT_EMAIL_LARGE_THRESHOLD
      ? email?.toString().trim().substring(0, 13) +
          ' ... ' +
          email
            ?.toString()
            .trim()
            .substring(email?.toString().trim().length - 14)
      : toTrimmedString(email)
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
    return ''
  }
}

export function formatEmailExtraExtraLarge(email: any): string {
  try {
    return isNotNullOrEmptyString(email) &&
      email?.toString().trim().length > FORMAT_EMAIL_EXTRA_EXTRA_LARGE_THRESHOLD
      ? email?.toString().trim().substring(0, 16) +
          ' ... ' +
          email
            ?.toString()
            .trim()
            .substring(email?.toString().trim().length - 31)
      : toTrimmedString(email)
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
    return ''
  }
}

export function formatEmailCrazyLarge(email: any): string {
  try {
    return isNotNullOrEmptyString(email) &&
      email?.toString().trim().length > FORMAT_EMAIL_CRAZY_LARGE_THRESHOLD
      ? email?.toString().trim().substring(0, 16) +
          ' ... ' +
          email
            ?.toString()
            .trim()
            .substring(email?.toString().trim().length - 31)
      : toTrimmedString(email)
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
    return ''
  }
}

export function isEmailTruncated(email: any, width: number): boolean {
  let ret: boolean = false
  try {
    if (isNotNullOrEmptyString(email)) {
      const trimmedLength: any = email.toString().trim().length
      if (width < 400) ret = trimmedLength > FORMAT_EMAIL_MEDIUM_THRESHOLD
      else ret = trimmedLength > FORMAT_EMAIL_LARGE_THRESHOLD
    }
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
  }
  return ret
}

export function formatUsername(username: any): string {
  try {
    return isNotNullOrEmptyString(username) &&
      username?.toString().trim().length > 10
      ? username?.toString().trim().substring(0, 4) +
          ' ... ' +
          username
            ?.toString()
            .trim()
            .substring(username?.toString().trim().length - 4)
      : toTrimmedString(username)
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
    return ''
  }
}

export function formatUsernameLong(username: any): string {
  try {
    return isNotNullOrEmptyString(username) &&
      username?.toString().trim().length > 11
      ? username?.toString().trim().substring(0, 5) +
          ' ... ' +
          username
            ?.toString()
            .trim()
            .substring(username?.toString().trim().length - 5)
      : toTrimmedString(username)
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
    return ''
  }
}

export async function saveUser(user: any) {
  return new Promise<boolean>(async (resolve) => {
    try {
      if (isNotNullOrEmptyString(user?.web2UserId)) {
        let subscribedDateNet: any = dateToNetDate(
          isNotNullOrEmptyString(user?.subscribed)
            ? dateToNetDate(user?.subscribed?.toString().trim())
            : null,
        )
        let subscriptionEndDateNet: any = dateToNetDate(
          isNotNullOrEmptyString(user?.subscriptionEnd)
            ? dateToNetDate(user?.subscriptionEnd?.toString().trim())
            : null,
        )
        const urlencoded: URLSearchParams = new URLSearchParams({
          userData: JSON.stringify({
            address: toTrimmedString(user?.address),
            chainId: toTrimmedString(user?.chainId),
            subscribed: subscribedDateNet,
            subscriptionEnd: subscriptionEndDateNet,
            darkModeDefault:
              user?.darkModeDefault != null ? user?.darkModeDefault : true,
            username: toTrimmedString(user?.username),
            bio: null,
            link: toTrimmedString(user?.link),
            avatarFileName: '',
            darkModeProfile:
              user?.darkModeProfile != null ? user?.darkModeProfile : true,
            newUser: user?.newUser ? 'true' : 'false',
            email: toTrimmedString(user?.email),
            emailVerified:
              user?.emailVerified != null ? user?.emailVerified : false,
            affiliateCode: null,
            web2UserId: toTrimmedString(user?.web2UserId),
            web2Email: toTrimmedString(user?.web2Email),
            web2Image: toTrimmedString(user?.web2Image),
            avatarFileNameArweave: '',
            emailNotifications:
              user?.emailNotifications != null
                ? user?.emailNotifications
                : false,
            defaultLanguage:
              user?.defaultLanguage != null ? user?.defaultLanguage : null,
            deviceRestartTime_Timezone:
              user?.deviceRestartTime_Timezone != null
                ? user?.deviceRestartTime_Timezone
                : null,
            deviceRestartTime_TimezoneLabel:
              user?.deviceRestartTime_TimezoneLabel != null
                ? user?.deviceRestartTime_TimezoneLabel
                : null,
            deviceRestartTime_TimezoneDstOffset:
              user?.deviceRestartTime_TimezoneDstOffset != null
                ? user?.deviceRestartTime_TimezoneDstOffset
                : null,
            deviceRestartTime_Timezone_UtcOffset:
              user?.deviceRestartTime_Timezone_UtcOffset != null
                ? user?.deviceRestartTime_Timezone_UtcOffset
                : null,
            deviceRestartTime:
              user?.deviceRestartTime != null ? user?.deviceRestartTime : null,
            adminWeb2UserId:
              user?.adminWeb2UserId != null ? user?.adminWeb2UserId : null,
            isHardware: user?.isHardware != null ? user?.isHardware : null,
            canSetSchedule:
              user?.canSetSchedule != null ? user?.canSetSchedule : null,
            canMakePlaylists:
              user?.canMakePlaylists != null ? user?.canMakePlaylists : null,
            canPlayPresets:
              user?.canPlayPresets != null ? user?.canPlayPresets : null,
            canPlayStations:
              user?.canPlayStations != null ? user?.canPlayStations : null,
            appReloadTime_Timezone:
              user?.appReloadTime_Timezone != null
                ? user?.appReloadTime_Timezone
                : null,
            appReloadTime_TimezoneLabel:
              user?.appReloadTime_TimezoneLabel != null
                ? user?.appReloadTime_TimezoneLabel
                : null,
            appReloadTime_TimezoneDstOffset:
              user?.appReloadTime_TimezoneDstOffset != null
                ? user?.appReloadTime_TimezoneDstOffset
                : null,
            appReloadTime_Timezone_UtcOffset:
              user?.appReloadTime_Timezone_UtcOffset != null
                ? user?.appReloadTime_Timezone_UtcOffset
                : null,
            appReloadTime:
              user?.appReloadTime != null ? user?.appReloadTime : null,
            displayName: user?.displayName != null ? user?.displayName : null,
            firstName: user?.firstName != null ? user?.firstName : null,
            lastName: user?.lastName != null ? user?.lastName : null,
            notes: user?.notes != null ? user?.notes : null,
            storeNumber: user?.storeNumber != null ? user?.storeNumber : null,
            addressOne: user?.addressOne != null ? user?.addressOne : null,
            addressTwo: user?.addressTwo != null ? user?.addressTwo : null,
            city: user?.city != null ? user?.city : null,
            stateProvince:
              user?.stateProvince != null ? user?.stateProvince : null,
            zipPostal: user?.zipPostal != null ? user?.zipPostal : null,
            country: user?.country != null ? user?.country : null,
          }),
        })
        const opts: any = {
          method: 'POST',
          body: urlencoded,
        }
        const resp: any = await fetch(HOST + '/api/p.stream-sv/u/pu', opts)
        const respon: any = await resp.json()
        resolve(respon?.success ? true : false)
      } else resolve(false)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve(false)
    }
  })
}

export async function reduceSubscriptionQuantity(
  stripeSubscriptionId: string,
  newQuantity: number,
) {
  return new Promise<boolean>(async (resolve) => {
    try {
      if (isNotNullOrEmptyString(stripeSubscriptionId) && newQuantity > -1) {
        const urlencoded: URLSearchParams = new URLSearchParams({
          subscriptionData: JSON.stringify({
            stripeSubscriptionId: toTrimmedString(stripeSubscriptionId),
            quantity: newQuantity,
          }),
        })
        const opts: any = {
          method: 'POST',
          body: urlencoded,
        }
        const resp: any = await fetch(HOST + '/api/ps-st/s/us', opts)
        const respon: any = await resp.json()
        resolve(respon?.success ? true : false)
      } else resolve(false)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve(false)
    }
  })
}

export async function unsubscribeBusinessUser(web2UserId: any) {
  return new Promise<boolean>(async (resolve) => {
    try {
      if (isNotNullOrEmptyString(web2UserId)) {
        const urlencoded: URLSearchParams = new URLSearchParams({
          userData: JSON.stringify({
            web2UserId: toTrimmedString(web2UserId),
          }),
        })
        const opts: any = {
          method: 'POST',
          body: urlencoded,
        }
        const resp: any = await fetch(HOST + '/api/p.stream-sv/u/ubu', opts)
        const respon: any = await resp.json()
        resolve(respon?.success ? true : false)
      } else resolve(false)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve(false)
    }
  })
}

export async function postAdminReportEnabled(
  web2UserId: any,
  adminReportEnabled: boolean,
) {
  return new Promise<any>(async (resolve) => {
    try {
      if (isNotNullOrEmptyString(web2UserId)) {
        const urlencoded: URLSearchParams = new URLSearchParams({
          userData: JSON.stringify({
            web2UserId: web2UserId,
            adminReportEnabled: adminReportEnabled,
          }),
        })
        const opts: any = {
          method: 'POST',
          body: urlencoded,
        }
        const resp: any = await fetch(HOST + '/api/p.stream-sv/u/pare', opts)
        const respon: any = await resp.json()
        resolve(respon?.success ? true : false)
      } else resolve(true)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve(true)
    }
  })
}

export async function unsubscribeUserFromEmails(web2Email: any) {
  return new Promise<any>(async (resolve) => {
    try {
      if (isNotNullOrEmptyString(web2Email)) {
        const urlencoded: URLSearchParams = new URLSearchParams({
          userData: JSON.stringify({
            web2Email: web2Email,
          }),
        })
        const opts: any = {
          method: 'POST',
          body: urlencoded,
        }
        const resp: any = await fetch(HOST + '/api/p.stream-sv/u/puu', opts)
        const respon: any = await resp.json()
        resolve(respon?.success ? true : false)
      } else resolve(true)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve(true)
    }
  })
}

export async function getBusinessUnsubscribedEmail(emailAddress: any) {
  return new Promise<any>(async (resolve) => {
    try {
      if (isNotNullOrEmptyString(emailAddress)) {
        const opts: any = {
          method: 'POST',
          body: JSON.stringify({
            emailAddress: emailAddress,
            emailTypeId: EMAIL_TYPE_ALL_EMAILS,
          }),
        }
        const resp: any = await fetch(HOST + '/api/p.stream-sv/ue/gue', opts)
        const respon: any = await resp.json()
        resolve(respon?.unsubscribedEmail)
      } else resolve(null)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve(null)
    }
  })
}

export const GET_SIGNOUT_MULTIPLE_LOCATIONS_CALLSTRING: string[] = [
  `self.addEventListener('message', async (event) => {
      if (event.data != undefined && event.data != null) {
        try {
          if (event?.data?.sessionUserId != null && 
              event?.data?.sessionUserId?.toString().trim() != '' &&
              event?.data?.lastLogin != null && 
              event?.data?.lastLogin?.toString().trim() != '') {
            const opts = {
              method: 'POST',
              body: JSON.stringify({
                sessionUserId: event.data.sessionUserId.toString().trim(),
                lastLogin: event.data.lastLogin.toString().trim(),
              }),
            }
            await fetch('${HOST}' + '/api/p.stream-sv/u/soml', opts)
              .then(async (x) => {
                const ret = await x.json()
                self.postMessage(ret?.needsSignout == true)
              })
              .catch((e) => {
                if (${DEBUG_MODE}) console.log(e)
                self.postMessage(false)
              })
          } else self.postMessage(false)
        } catch (e) {
          if (${DEBUG_MODE}) console.log(e)
          self.postMessage(false)
        }
      } else self.postMessage(false)
    })`,
]

export const GET_USER_CALLSTRING: string[] = [
  `self.addEventListener('message', async (event) => {
      if (event.data != undefined && event.data != null && event.data.toString().trim() != '') {
        try {
          if (event?.data?.sessionUserId != null && event?.data?.sessionUserId?.toString().trim() != '') {
            const opts = {
              method: 'POST',
              body: JSON.stringify({
                sessionUserId: event.data.sessionUserId.toString().trim(),
                email: event.data.email != null ? event.data.email.toString().trim() : '',
                image: '',
              }),
            }
            await fetch('${HOST}' + '/api/p.stream-sv/u/gu2', opts)
              .then(async (x) => {
                const ret = await x.json()
                self.postMessage(ret?.user)
              })
              .catch((e) => {
                if (${DEBUG_MODE}) console.log(e)
                self.postMessage(null)
              })
          } else self.postMessage(null)
        } catch (e) {
          if (${DEBUG_MODE}) console.log(e)
          self.postMessage(null)
        }
      } else self.postMessage(null)
    })`,
]

export async function getUser(sessionUserId: any, email: any) {
  return new Promise<any>(async (resolve) => {
    try {
      if (isNotNullOrEmptyString(sessionUserId)) {
        const opts: any = {
          method: 'POST',
          body: JSON.stringify({
            sessionUserId: toTrimmedString(sessionUserId),
            email: toTrimmedString(email),
            image: '',
          }),
        }
        await fetch(HOST + '/api/p.stream-sv/u/gu2', opts)
          .then(async (x: any) => {
            const ret: any = await x.json()
            resolve(ret?.user)
          })
          .catch((e: any) => {
            if (DEBUG_MODE) console.log(e)
            resolve(null)
          })
      } else resolve(null)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve(null)
    }
  })
}

export async function getSubscriptionUsers(adminWeb2UserId: any) {
  return new Promise<any>(async (resolve) => {
    try {
      if (isNotNullOrEmptyString(adminWeb2UserId)) {
        const opts: any = {
          method: 'POST',
          body: JSON.stringify({
            adminWeb2UserId: toTrimmedString(adminWeb2UserId),
          }),
        }
        await fetch(HOST + '/api/p.stream-sv/u/gsu', opts)
          .then(async (x: any) => {
            const ret: any = await x.json()
            if (DEBUG_MODE) console.log(ret?.users)
            resolve(ret?.users)
          })
          .catch((e: any) => {
            if (DEBUG_MODE) console.log(e)
            resolve([])
          })
      } else resolve([])
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve([])
    }
  })
}

export function hasWeb2UserImage(session: any) {
  let ret: boolean = false
  try {
    ret = isNotNullOrEmptyString(session?.user?.image)
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
  }
  return ret
}

export function isWeb2UserLoggedIn(session: any) {
  let ret: boolean = false
  try {
    ret = isNotNullOrEmptyString(session?.user?.id)
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
  }
  return ret
}

export function getLastLogin(session: any) {
  let ret: Date | null = null
  try {
    ret = isNotNullOrEmptyString(session?.user?.lastLogin)
      ? new Date(session?.user?.lastLogin)
      : null
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
  }
  return ret
}

export async function signOutOnAllDevices(web2UserId: any) {
  return new Promise<any>(async (resolve) => {
    try {
      if (isNotNullOrEmptyString(web2UserId)) {
        const urlencoded: URLSearchParams = new URLSearchParams({
          userData: JSON.stringify({
            sessionUserId: web2UserId,
          }),
        })
        const opts: any = {
          method: 'POST',
          body: urlencoded,
        }
        const resp: any = await fetch(HOST + '/api/p.stream-sv/u/somle', opts)
        const respon: any = await resp.json()
        resolve(respon?.success ? true : false)
      } else resolve(false)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve(false)
    }
  })
}

export function getWeb2UserId(session: any) {
  let ret: any = null
  try {
    ret = toTrimmedString(session?.user?.id)
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
  }
  return ret
}

export function getWeb2Email(session: any) {
  let ret: any = null
  try {
    ret = toTrimmedString(session?.user?.email)
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
  }
  return ret
}

export function isUserSubscribed(user: any) {
  let ret: boolean = false
  try {
    if (
      isNullOrUndefined(user) ||
      isNullOrEmptyString(user?.subscribed) ||
      isNullOrEmptyString(user?.subscriptionEnd) ||
      isNullOrEmptyString(user?.web2UserId)
    )
      ret = false
    else if (isNotNullOrEmptyString(user?.subscriptionEnd)) {
      let subscriptionEnd: any = new Date(user?.subscriptionEnd)
      subscriptionEnd.setDate(subscriptionEnd.getDate() + 1)
      const today: Date = new Date()
      ret = today <= subscriptionEnd
    }
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
  }
  return ret
}

export function isUserMaybeSubscribed(user: any) {
  let ret: boolean = false
  try {
    if (
      isNullOrUndefined(user) ||
      isNullOrEmptyString(user?.subscribed) ||
      isNullOrEmptyString(user?.subscriptionEnd) ||
      isNullOrEmptyString(user?.web2UserId)
    )
      ret = false
    else if (isNotNullOrEmptyString(user?.subscriptionEnd)) {
      let subscriptionEnd: any = new Date(user?.subscriptionEnd)
      subscriptionEnd.setDate(subscriptionEnd.getDate() + 1)
      const min: Date = new Date('2000-01-01')
      ret = min <= subscriptionEnd
    }
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
  }
  return ret
}

export async function getStripeCurrency(stripeCustomerId: any) {
  return new Promise<any>(async (resolve) => {
    let ret: any = null
    try {
      if (isNotNullOrEmptyString(stripeCustomerId)) {
        let urlencoded: URLSearchParams = new URLSearchParams({
          customerData: JSON.stringify({
            stripeCustomerId: toTrimmedString(stripeCustomerId),
          }),
        })
        let opts: any = {
          method: 'POST',
          body: urlencoded,
        }
        await fetch(HOST + '/api/ps-st/cu/cur', opts)
          .then(async (x: any) => {
            ret = await x.json()
            ret = ret?.cur
          })
          .catch((e: any) => {
            ret = e
            if (DEBUG_MODE) console.log(e)
          })
        return resolve(ret)
      } else resolve(null)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve(null)
    }
  })
}

export async function getCustomer(
  customerId: any,
  stripeCustomerId: any,
  web2UserSessionId: any,
) {
  return new Promise<any>(async (resolve) => {
    let ret: any = null
    try {
      if (isNotNullOrEmptyString(web2UserSessionId)) {
        let urlencoded: URLSearchParams = new URLSearchParams({
          customerData: JSON.stringify({
            customerId: toTrimmedString(customerId),
            stripeCustomerId: toTrimmedString(stripeCustomerId),
            web2UserId: toTrimmedString(web2UserSessionId),
          }),
        })
        let opts: any = {
          method: 'POST',
          body: urlencoded,
        }
        await fetch(HOST + '/api/ps-st/cu/r', opts)
          .then(async (x: any) => {
            ret = await x.json()
            ret = ret?.stripeCustomer
          })
          .catch((e: any) => {
            ret = e
            if (DEBUG_MODE) console.log(e)
          })
        return resolve(ret)
      } else resolve(null)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve(null)
    }
  })
}

export async function getPrice(productId: string) {
  return new Promise<any>(async (resolve) => {
    let ret: any = null
    try {
      if (isNotNullOrEmptyString(productId)) {
        let urlencoded: URLSearchParams = new URLSearchParams({
          priceData: JSON.stringify({
            stripeProductId: toTrimmedString(productId),
          }),
        })
        let opts: any = {
          method: 'POST',
          body: urlencoded,
        }
        await fetch(HOST + '/api/ps-st/pr/gp', opts)
          .then(async (x: any) => {
            ret = await x.json()
            ret = ret?.price
          })
          .catch((e: any) => {
            ret = e
            if (DEBUG_MODE) console.log(e)
          })
        return resolve(ret)
      } else resolve(null)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve(null)
    }
  })
}

export async function getSubscriptions(stripeCustomerId: any) {
  return new Promise<any>(async (resolve) => {
    let ret: any = null
    try {
      if (isNotNullOrEmptyString(stripeCustomerId)) {
        let urlencoded: URLSearchParams = new URLSearchParams({
          subscriptionData: JSON.stringify({
            stripeCustomerId: toTrimmedString(stripeCustomerId),
          }),
        })
        let opts: any = {
          method: 'POST',
          body: urlencoded,
        }
        await fetch(HOST + '/api/ps-st/s/gs', opts)
          .then(async (x: any) => {
            ret = await x.json()
            if (!ret?.success) {
              if (DEBUG_MODE) console.log('xva - 1')
              if (!IS_PRODUCTION) toastError(LANGUAGE_CODE_ENGLISH)
            }
            ret = ret?.subscriptions?.length > 0 ? ret?.subscriptions : []
          })
          .catch((e: any) => {
            ret = e
            if (DEBUG_MODE) console.log(e)
          })
        return resolve(ret)
      } else resolve(null)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve(null)
    }
  })
}

export async function getCustomerPortalSession(
  stripeCustomerId: any,
  stripeLocale: any,
) {
  return new Promise<any>(async (resolve) => {
    let ret: any = null
    try {
      if (isNotNullOrEmptyString(stripeCustomerId)) {
        let urlencoded: URLSearchParams = new URLSearchParams({
          customerData: JSON.stringify({
            stripeCustomerId: toTrimmedString(stripeCustomerId),
            stripeLocale: toTrimmedStringLowerCase(stripeLocale),
          }),
        })
        let opts: any = {
          method: 'POST',
          body: urlencoded,
        }
        await fetch(HOST + '/api/ps-st/cu/bp', opts)
          .then(async (x: any) => {
            ret = await x.json()
            ret = ret?.session
          })
          .catch((e: any) => {
            ret = e
            if (DEBUG_MODE) console.log(e)
          })
        return resolve(ret)
      } else resolve(null)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve(null)
    }
  })
}

export async function getCustomerPortalTemporaryBillingManagementLink(
  stripeCustomerId: any,
  stripeLocale: any,
) {
  return new Promise<string | null>(async (resolve) => {
    let ret: string | null = null
    try {
      if (isNotNullOrEmptyString(stripeCustomerId)) {
        let urlencoded: URLSearchParams = new URLSearchParams({
          customerData: JSON.stringify({
            stripeCustomerId: toTrimmedString(stripeCustomerId),
            stripeLocale: toTrimmedStringLowerCase(stripeLocale),
          }),
        })
        let opts: any = {
          method: 'POST',
          body: urlencoded,
        }
        await fetch(HOST + '/api/ps-st/cu/tbl', opts)
          .then(async (x: any) => {
            const resp: any = await x.json()
            ret = isNotNullOrEmptyString(resp?.url)
              ? toTrimmedString(resp?.url)
              : null
          })
          .catch((e: any) => {
            if (DEBUG_MODE) console.log(e)
          })
        return resolve(ret)
      } else resolve(null)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve(null)
    }
  })
}

export async function cancelSubscription(stripeSubscriptionId: any) {
  return new Promise<boolean>(async (resolve) => {
    let ret: boolean = false
    try {
      if (isNotNullOrEmptyString(stripeSubscriptionId)) {
        let urlencoded: URLSearchParams = new URLSearchParams({
          subscriptionData: JSON.stringify({
            stripeSubscriptionId: toTrimmedString(stripeSubscriptionId),
          }),
        })
        let opts: any = {
          method: 'POST',
          body: urlencoded,
        }
        await fetch(HOST + '/api/ps-st/s/ca', opts)
          .then(async (x: any) => {
            const rez: any = await x.json()
            ret = rez?.success ? true : false
          })
          .catch((e: any) => {
            ret = false
            if (DEBUG_MODE) console.log(e)
          })
        return resolve(ret)
      } else resolve(false)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve(false)
    }
  })
}

export async function notifyUserUnsubscribed(
  stripeSubscriptionId: any,
  adminWeb2UserId: any,
  adminWeb2Email: any,
  stripeCustomerId: any,
  reducedQuantity: number | null,
) {
  return new Promise<boolean>(async (resolve) => {
    let ret: boolean = false
    try {
      if (isNotNullOrEmptyString(adminWeb2UserId)) {
        let urlencoded: URLSearchParams = new URLSearchParams({
          userData:
            reducedQuantity != null
              ? JSON.stringify({
                  stripeSubscriptionId: toTrimmedString(stripeSubscriptionId),
                  adminWeb2UserId: toTrimmedString(adminWeb2UserId),
                  adminWeb2Email: toTrimmedString(adminWeb2Email),
                  stripeCustomerId: toTrimmedString(stripeCustomerId),
                  reducedQuantity: toTrimmedString(reducedQuantity),
                  unsubscribeFunction: 'cancelSubscription',
                })
              : JSON.stringify({
                  stripeSubscriptionId: toTrimmedString(stripeSubscriptionId),
                  adminWeb2UserId: toTrimmedString(adminWeb2UserId),
                  adminWeb2Email: toTrimmedString(adminWeb2Email),
                  stripeCustomerId: toTrimmedString(stripeCustomerId),
                  unsubscribeFunction: 'cancelSubscription',
                }),
        })
        let opts: any = {
          method: 'POST',
          body: urlencoded,
        }
        await fetch(HOST + '/api/p.stream-sv/u/nus', opts)
          .then(async (x: any) => {
            const rez: any = await x.json()
            ret = rez?.success ? true : false
          })
          .catch((e: any) => {
            ret = false
            if (DEBUG_MODE) console.log(e)
          })
        return resolve(ret)
      } else resolve(false)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve(false)
    }
  })
}

export async function notifyHardwareUserDeleted(
  adminWeb2UserId: string,
  adminWeb2Email: any,
  stripeCustomerId: any,
  stripeSubscriptionId: any,
) {
  return new Promise<boolean>(async (resolve) => {
    try {
      if (isNotNullOrEmptyString(adminWeb2UserId)) {
        let urlencoded: URLSearchParams = new URLSearchParams({
          notifyData: JSON.stringify({
            adminWeb2UserId: toTrimmedString(adminWeb2UserId),
            adminWeb2Email: toTrimmedString(adminWeb2Email),
            stripeCustomerId: toTrimmedString(stripeCustomerId),
            stripeSubscriptionId: toTrimmedString(stripeSubscriptionId),
          }),
        })
        let ret: boolean = false
        let opts: any = {
          method: 'POST',
          body: urlencoded,
        }
        await fetch(HOST + '/api/p.stream-sv/u/nhud', opts)
          .then(async (x: any) => {
            const rez: any = await x.json()
            ret = rez?.success ? true : false
            resolve(ret)
          })
          .catch((e: any) => {
            if (DEBUG_MODE) console.log(e)
            resolve(false)
          })
      } else resolve(false)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve(false)
    }
  })
}

export async function getCharges(stripeCustomerId: any) {
  return new Promise<any>(async (resolve) => {
    try {
      if (isNotNullOrEmptyString(stripeCustomerId)) {
        let urlencoded: URLSearchParams = new URLSearchParams({
          chargeData: JSON.stringify({
            stripeCustomerId: toTrimmedString(stripeCustomerId),
          }),
        })
        let opts: any = {
          method: 'POST',
          body: urlencoded,
        }
        await fetch(HOST + '/api/ps-st/ch/gchs', opts)
          .then(async (x: any) => {
            const rez: any = await x.json()
            if (!rez?.success) {
              if (DEBUG_MODE) console.log('xva - 2')
              if (!IS_PRODUCTION) toastError(LANGUAGE_CODE_ENGLISH)
            }
            return resolve({
              charges: rez?.charges?.length > 0 ? rez?.charges : [],
              success: rez?.success,
            })
          })
          .catch((e: any) => {
            if (DEBUG_MODE) console.log(e)
            resolve({ charges: [], success: false })
          })
      } else resolve({ charges: [], success: false })
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve({ charges: [], success: false })
    }
  })
}

export async function createCustomer(
  userId: any,
  web2UserId: any,
  web2Email: any,
) {
  let ret: any = null
  try {
    if (isNotNullOrEmptyString(userId) || isNotNullOrEmptyString(web2UserId)) {
      let urlencoded: URLSearchParams = new URLSearchParams({
        customerData: JSON.stringify({
          userId: toTrimmedString(userId),
          web2UserId: toTrimmedString(web2UserId),
          stripeCustomerId: null,
          stripeEmail: toTrimmedString(web2Email),
        }),
      })
      let opts: any = {
        method: 'POST',
        body: urlencoded,
      }
      let resp: any = await fetch(HOST + '/api/ps-st/cu/cr', opts)
      let respon: any = await resp.json()
      ret = respon?.stripeCustomer
    }
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
  }
  return ret
}

export function formatStripeFundsAmount(
  amount: number,
  currency: string,
  selectedLanguage: string,
) {
  let ret: string = isNotNullOrEmptyString(amount)
    ? formatTwoDecimalPoints(amount / 100)
    : '0.00'
  try {
    const locale = translate(
      LANGUAGE_CODE_ENGLISH + '-US',
      LANGUAGE_CODE_SPANISH + '-ES',
      LANGUAGE_CODE_FRENCH + '-FR',
      LANGUAGE_CODE_KOREAN + '-KR',
      selectedLanguage,
    )
    ret = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount / 100)
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
    ret = `${currency.toUpperCase()} ${amount.toFixed(2)}`
  }
  return ret
}

export function getLastSevenOfStripeId(value: any): string {
  let ret: string = ''
  try {
    const trimmedValue =
      value !== null && value !== undefined ? String(value).trim() : ''

    if (trimmedValue.length >= 7) {
      ret = trimmedValue.slice(-7)
    } else {
      ret = trimmedValue
    }
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
  }
  return ret
}

export function formatStripeSubscriptionsExport(
  selectedLanguage: any,
  c: any,
  convertedStripePrice: any,
  stripeOrVercelHeaderBasedCurrency: any,
) {
  let ret: any = null
  try {
    if (isKorean(selectedLanguage)) {
      ret = {
        기간:
          formatUnixTimestamp(
            c?.current_period_start,
            selectedLanguage,
            stripeOrVercelHeaderBasedCurrency,
          ) +
          ' - ' +
          formatUnixTimestamp(
            c?.current_period_end,
            selectedLanguage,
            stripeOrVercelHeaderBasedCurrency,
          ),
        식별자: getLastSevenOfStripeId(c?.id),
        수량: c?.quantity,
        가격: convertedStripePrice,
        빈도:
          c?.plan?.interval == STRIPE_SUBSCRIPTION_INTERVAL_YEARLY
            ? '매년'
            : c?.plan?.interval == STRIPE_SUBSCRIPTION_INTERVAL_MONTHLY
              ? '월간 간행물'
              : '',
        상태:
          c?.status == STRIPE_SUBSCRIPTION_STATUS_CANCELED ||
          isNotNullOrEmptyString(c?.cancel_at)
            ? '취소'
            : c?.status == STRIPE_SUBSCRIPTION_STATUS_INCOMPLETE
              ? '불완전한'
              : c?.status == STRIPE_SUBSCRIPTION_STATUS_TRIALING
                ? '무료 평가판'
                : c?.status == STRIPE_SUBSCRIPTION_STATUS_PAST_DUE
                  ? '늦은'
                  : c?.status == STRIPE_SUBSCRIPTION_STATUS_UNPAID
                    ? '미지급'
                    : c?.status == STRIPE_SUBSCRIPTION_STATUS_PAUSED
                      ? '일시중지됨'
                      : c?.status == STRIPE_SUBSCRIPTION_STATUS_ACTIVE
                        ? '활성화됨'
                        : c?.status ==
                            STRIPE_SUBSCRIPTION_STATUS_INCOMPLETE_EXPIRED
                          ? '만료됨'
                          : '알려지지 않은',
      }
    } else if (isSpanish(selectedLanguage)) {
      ret = {
        Periodo:
          formatUnixTimestamp(
            c?.current_period_start,
            selectedLanguage,
            stripeOrVercelHeaderBasedCurrency,
          ) +
          ' - ' +
          formatUnixTimestamp(
            c?.current_period_end,
            selectedLanguage,
            stripeOrVercelHeaderBasedCurrency,
          ),
        Identificador: getLastSevenOfStripeId(c?.id),
        Cantidad: c?.quantity,
        Precio: convertedStripePrice,
        Frecuencia:
          c?.plan?.interval == STRIPE_SUBSCRIPTION_INTERVAL_YEARLY
            ? 'Anual'
            : c?.plan?.interval == STRIPE_SUBSCRIPTION_INTERVAL_MONTHLY
              ? 'Mensual'
              : '',
        Estado:
          c?.status == STRIPE_SUBSCRIPTION_STATUS_CANCELED ||
          isNotNullOrEmptyString(c?.cancel_at)
            ? 'Cancelado'
            : c?.status == STRIPE_SUBSCRIPTION_STATUS_INCOMPLETE
              ? 'Incompleto'
              : c?.status == STRIPE_SUBSCRIPTION_STATUS_TRIALING
                ? 'Prueba Gratuita'
                : c?.status == STRIPE_SUBSCRIPTION_STATUS_PAST_DUE
                  ? 'Atrasado'
                  : c?.status == STRIPE_SUBSCRIPTION_STATUS_UNPAID
                    ? 'No Pagado'
                    : c?.status == STRIPE_SUBSCRIPTION_STATUS_PAUSED
                      ? 'Pausado'
                      : c?.status == STRIPE_SUBSCRIPTION_STATUS_ACTIVE
                        ? 'Activado'
                        : c?.status ==
                            STRIPE_SUBSCRIPTION_STATUS_INCOMPLETE_EXPIRED
                          ? 'Caducado'
                          : 'Desconocida',
      }
    } else if (isFrench(selectedLanguage)) {
      ret = {
        Période:
          formatUnixTimestamp(
            c?.current_period_start,
            selectedLanguage,
            stripeOrVercelHeaderBasedCurrency,
          ) +
          ' - ' +
          formatUnixTimestamp(
            c?.current_period_end,
            selectedLanguage,
            stripeOrVercelHeaderBasedCurrency,
          ),
        Identifiant: getLastSevenOfStripeId(c?.id),
        Quantité: c?.quantity,
        Prix: convertedStripePrice,
        Fréquence:
          c?.plan?.interval == STRIPE_SUBSCRIPTION_INTERVAL_YEARLY
            ? 'Anual'
            : c?.plan?.interval == STRIPE_SUBSCRIPTION_INTERVAL_MONTHLY
              ? 'Mensual'
              : '',
        Statut:
          c?.status == STRIPE_SUBSCRIPTION_STATUS_CANCELED ||
          isNotNullOrEmptyString(c?.cancel_at)
            ? 'Annulé'
            : c?.status == STRIPE_SUBSCRIPTION_STATUS_INCOMPLETE
              ? 'Incomplet'
              : c?.status == STRIPE_SUBSCRIPTION_STATUS_TRIALING
                ? 'Essai Gratuit'
                : c?.status == STRIPE_SUBSCRIPTION_STATUS_PAST_DUE
                  ? 'En Retard'
                  : c?.status == STRIPE_SUBSCRIPTION_STATUS_UNPAID
                    ? 'Non Rémunéré'
                    : c?.status == STRIPE_SUBSCRIPTION_STATUS_PAUSED
                      ? 'En Pause'
                      : c?.status == STRIPE_SUBSCRIPTION_STATUS_ACTIVE
                        ? 'Activé'
                        : c?.status ==
                            STRIPE_SUBSCRIPTION_STATUS_INCOMPLETE_EXPIRED
                          ? 'Expiré'
                          : 'Inconnu',
      }
    } else
      ret = {
        Period:
          formatUnixTimestamp(
            c?.current_period_start,
            selectedLanguage,
            stripeOrVercelHeaderBasedCurrency,
          ) +
          ' - ' +
          formatUnixTimestamp(
            c?.current_period_end,
            selectedLanguage,
            stripeOrVercelHeaderBasedCurrency,
          ),
        Identifier: getLastSevenOfStripeId(c?.id),
        Quantity: c?.quantity,
        Price: convertedStripePrice,
        Frequency:
          c?.plan?.interval == STRIPE_SUBSCRIPTION_INTERVAL_YEARLY
            ? 'Yearly'
            : c?.plan?.interval == STRIPE_SUBSCRIPTION_INTERVAL_MONTHLY
              ? 'Monthly'
              : '',
        Status:
          c?.status == STRIPE_SUBSCRIPTION_STATUS_CANCELED ||
          isNotNullOrEmptyString(c?.cancel_at)
            ? 'Canceled'
            : c?.status == STRIPE_SUBSCRIPTION_STATUS_INCOMPLETE
              ? 'Incomplete'
              : c?.status == STRIPE_SUBSCRIPTION_STATUS_TRIALING
                ? 'Trial'
                : c?.status == STRIPE_SUBSCRIPTION_STATUS_PAST_DUE
                  ? 'Past Due'
                  : c?.status == STRIPE_SUBSCRIPTION_STATUS_UNPAID
                    ? 'Unpaid'
                    : c?.status == STRIPE_SUBSCRIPTION_STATUS_PAUSED
                      ? 'Paused'
                      : c?.status == STRIPE_SUBSCRIPTION_STATUS_ACTIVE
                        ? 'Active'
                        : c?.status ==
                            STRIPE_SUBSCRIPTION_STATUS_INCOMPLETE_EXPIRED
                          ? 'Expired'
                          : 'Unknown',
      }
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
  }
  return ret
}

export function formatInvitationsExport(selectedLanguage: any, c: any) {
  let ret: any = null
  try {
    if (isKorean(selectedLanguage)) {
      ret = {
        초대됨: netDateToDateNicelyFormatted(
          c?.created,
          selectedLanguage,
          false,
        ),
        에게: toTrimmedString(c?.email),
        주소: makeInvitationLink(c?.invitationToken),
      }
    } else if (isSpanish(selectedLanguage)) {
      ret = {
        Invitada: netDateToDateNicelyFormatted(
          c?.created,
          selectedLanguage,
          false,
        ),
        Para: toTrimmedString(c?.email),
        URL: makeInvitationLink(c?.invitationToken),
      }
    } else if (isFrench(selectedLanguage)) {
      ret = {
        Invitée: netDateToDateNicelyFormatted(
          c?.created,
          selectedLanguage,
          false,
        ),
        Destinataire: toTrimmedString(c?.email),
        Adresse: makeInvitationLink(c?.invitationToken),
      }
    } else
      ret = {
        Invited: netDateToDateNicelyFormatted(
          c?.created,
          selectedLanguage,
          false,
        ),
        Recipient: toTrimmedString(c?.email),
        Link: makeInvitationLink(c?.invitationToken),
      }
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
  }
  return ret
}

export function formatUsersExport(selectedLanguage: any, c: any) {
  let ret: any = null
  try {
    const minSubscriptionOrAcceptedOnDate: Date = new Date('1970-01-01')
    if (isKorean(selectedLanguage)) {
      ret = {
        생성됨:
          isDeactivationScheduledYear(c?.subscribed) == true ||
          c?.subscribed == USER_SCHEDULED_FOR_DELETION_SUBSCRIBED_STR
            ? '취소'
            : !c?.isHardware
              ? netDateToDateNicelyFormatted(
                  c?.acceptedInvitation,
                  selectedLanguage,
                  false,
                )
              : isNotNullOrEmptyString(c?.subscribed) &&
                  netDateToDateOrEmptyString(c?.subscribed) != '' &&
                  netDateToDateOrEmptyString(c?.subscribed) >
                    minSubscriptionOrAcceptedOnDate
                ? netDateToDateNicelyFormatted(
                    c?.subscribed,
                    selectedLanguage,
                    false,
                  )
                : '',
        사용자:
          isDeactivationScheduledYear(c?.subscribed) == true ||
          c?.subscribed == USER_SCHEDULED_FOR_DELETION_SUBSCRIBED_STR
            ? '취소'
            : c?.web2UserId == PROVISIONING_HARDWARE_PLACEHOLDER
              ? '배송 대기 중'
              : toTrimmedString(c?.web2Email),
        하드웨어: c?.isHardware ? '예' : '',
      }
    } else if (isSpanish(selectedLanguage)) {
      ret = {
        Creado:
          isDeactivationScheduledYear(c?.subscribed) == true ||
          c?.subscribed == USER_SCHEDULED_FOR_DELETION_SUBSCRIBED_STR
            ? 'CANCELADA'
            : !c?.isHardware
              ? netDateToDateNicelyFormatted(
                  c?.acceptedInvitation,
                  selectedLanguage,
                  false,
                )
              : isNotNullOrEmptyString(c?.subscribed) &&
                  netDateToDateOrEmptyString(c?.subscribed) != '' &&
                  netDateToDateOrEmptyString(c?.subscribed) >
                    minSubscriptionOrAcceptedOnDate
                ? netDateToDateNicelyFormatted(
                    c?.subscribed,
                    selectedLanguage,
                    false,
                  )
                : '',
        Usuario:
          isDeactivationScheduledYear(c?.subscribed) == true ||
          c?.subscribed == USER_SCHEDULED_FOR_DELETION_SUBSCRIBED_STR
            ? 'CANCELADA'
            : c?.web2UserId == PROVISIONING_HARDWARE_PLACEHOLDER
              ? 'En Espera de Envío'
              : toTrimmedString(c?.web2Email),
        Hardware: c?.isHardware ? 'Si' : '',
      }
    } else if (isFrench(selectedLanguage)) {
      ret = {
        Créé:
          isDeactivationScheduledYear(c?.subscribed) == true ||
          c?.subscribed == USER_SCHEDULED_FOR_DELETION_SUBSCRIBED_STR
            ? 'ANNULÉ'
            : !c?.isHardware
              ? netDateToDateNicelyFormatted(
                  c?.acceptedInvitation,
                  selectedLanguage,
                  false,
                )
              : isNotNullOrEmptyString(c?.subscribed) &&
                  netDateToDateOrEmptyString(c?.subscribed) != '' &&
                  netDateToDateOrEmptyString(c?.subscribed) >
                    minSubscriptionOrAcceptedOnDate
                ? netDateToDateNicelyFormatted(
                    c?.subscribed,
                    selectedLanguage,
                    false,
                  )
                : '',
        Utilisateur:
          isDeactivationScheduledYear(c?.subscribed) == true ||
          c?.subscribed == USER_SCHEDULED_FOR_DELETION_SUBSCRIBED_STR
            ? 'ANNULÉ'
            : c?.web2UserId == PROVISIONING_HARDWARE_PLACEHOLDER
              ? "En Attente d'Expédition"
              : toTrimmedString(c?.web2Email),
        Matériel: c?.isHardware ? 'Oui' : '',
      }
    } else
      ret = {
        Created:
          isDeactivationScheduledYear(c?.subscribed) == true ||
          c?.subscribed == USER_SCHEDULED_FOR_DELETION_SUBSCRIBED_STR
            ? 'CANCELED'
            : !c?.isHardware
              ? netDateToDateNicelyFormatted(
                  c?.acceptedInvitation,
                  selectedLanguage,
                  false,
                )
              : isNotNullOrEmptyString(c?.subscribed) &&
                  netDateToDateOrEmptyString(c?.subscribed) != '' &&
                  netDateToDateOrEmptyString(c?.subscribed) >
                    minSubscriptionOrAcceptedOnDate
                ? netDateToDateNicelyFormatted(
                    c?.subscribed,
                    selectedLanguage,
                    false,
                  )
                : '',
        User:
          isDeactivationScheduledYear(c?.subscribed) == true ||
          c?.subscribed == USER_SCHEDULED_FOR_DELETION_SUBSCRIBED_STR
            ? 'CANCELED'
            : c?.web2UserId == PROVISIONING_HARDWARE_PLACEHOLDER
              ? 'Awaiting Shipment'
              : toTrimmedString(c?.web2Email),
        Hardware: c?.isHardware ? 'Yes' : '',
      }
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
  }
  return ret
}

function getPaymentMethodDisplay(details: any, selectedLanguage: any) {
  let ret: any = ''
  try {
    const type = details?.type
    const brandMapping: any = {
      visa: 'Visa',
      mastercard: 'MasterCard',
      discover: 'Discover',
      amex: 'AmEx',
      diners: 'Diners',
      jcb: 'JCB',
      unionpay: 'UnionPay',
      eftpos_au: 'EFTPOS AU',
      amazon_pay: 'Amazon Pay',
      cashapp: 'CashApp',
    }
    if (type === 'card' && details?.card) {
      const brand = details.card.brand
      const last4 = toTrimmedString(details.card.last4)
      const brandDisplay = brandMapping[brand]
        ? ` (${brandMapping[brand]})`
        : ''
      ret = `x${last4}${brandDisplay}`
    } else if (type === 'link') {
      const group = details?.link?.funding_source_group || ''
      if (group.includes('000') || group.includes('card')) {
        ret = translate(
          'Card (via Stripe Link)',
          'Tarjeta (vía Stripe Link)',
          'Carte (via Stripe Link)',
          '카드 (Stripe Link 경유)',
          selectedLanguage,
        )
      } else if (group.includes('bank') || group.includes('ach')) {
        ret = translate(
          'Bank Account (via Stripe Link)',
          'Cuenta bancaria (vía Stripe Link)',
          'Compte bancaire (via Stripe Link)',
          '은행 계좌 (Stripe Link 경유)',
          selectedLanguage,
        )
      } else {
        ret = translate(
          'Stripe Link',
          'Stripe Link',
          'Stripe Link',
          'Stripe Link',
          selectedLanguage,
        )
      }
    } else if (type && details[type]) {
      const subDetails = details[type]
      let localizedType = ''
      if (type === 'us_bank_account') {
        localizedType = translate(
          'US Bank Account',
          'Cuenta bancaria de EE. UU.',
          'Compte bancaire américain',
          '미국 은행 계좌',
          selectedLanguage,
        )
      } else if (type === 'sepa_debit') {
        localizedType = translate(
          'SEPA Direct Debit',
          'Débito directo SEPA',
          'Prélèvement SEPA',
          'SEPA 직불 결제',
          selectedLanguage,
        )
      } else {
        localizedType = type
          .split('_')
          .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      }
      if (subDetails?.last4) {
        ret = `x${toTrimmedString(subDetails.last4)} (${localizedType})`
      } else {
        ret = localizedType
      }
    } else {
      ret = type
        ? `${translate('Unknown', 'Desconocido', 'Inconnu', '알 수 없음', selectedLanguage)} (${type})`
        : translate(
            'Unknown Method',
            'Método desconocido',
            'Méthode inconnue',
            '알 수 없는 결제 수단',
            selectedLanguage,
          )
    }
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
  }
  return ret
}

export function getExportFileName(baseName: string, extension: string) {
  let ret: string = ''
  try {
    const d = new Date()
    const pad = (n: number) => (n < 10 ? `0${n}` : n)
    const year = d.getFullYear()
    const month = pad(d.getMonth() + 1)
    const day = pad(d.getDate())
    const hours = pad(d.getHours())
    const mins = pad(d.getMinutes())
    const secs = pad(d.getSeconds())
    const cleanBase = baseName.trim().replace(/\s+/g, '_')
    ret = `${cleanBase}_${year}-${month}-${day}_${hours}_${mins}_${secs}.${extension}`
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
  }
  return ret
}

export function formatStripeChargesExport(
  selectedLanguage: any,
  c: any,
  localCurrency: any,
) {
  let ret: any = null
  try {
    const chargeCurrency: any = c?.currency
      ? String(c.currency).toUpperCase()
      : localCurrency
    if (isKorean(selectedLanguage)) {
      ret = {
        날짜: formatUnixTimestamp(c?.created, selectedLanguage, localCurrency),
        참조번호: getLastSevenOfStripeId(c?.id),
        방법: getPaymentMethodDisplay(
          c?.payment_method_details,
          selectedLanguage,
        ),
        총액: formatStripeFundsAmount(
          c?.amount,
          isNotNullOrEmptyString(c?.currency)
            ? toTrimmedString(c?.currency)
            : chargeCurrency,
          selectedLanguage,
        ),
        상태: c?.disputed
          ? '검토중'
          : c?.refunded
            ? '환불됨'
            : c?.paid
              ? '유급의'
              : c?.status == 'failed'
                ? '실패한'
                : '보류 중',
        하이퍼링크: toTrimmedString(c?.receipt_url),
      }
    } else if (isSpanish(selectedLanguage)) {
      ret = {
        Fecha: formatUnixTimestamp(c?.created, selectedLanguage, localCurrency),
        Referencia: getLastSevenOfStripeId(c?.id),
        Método: getPaymentMethodDisplay(
          c?.payment_method_details,
          selectedLanguage,
        ),
        Suma: formatStripeFundsAmount(
          c?.amount,
          isNotNullOrEmptyString(c?.currency)
            ? toTrimmedString(c?.currency)
            : chargeCurrency,
          selectedLanguage,
        ),
        Estado: c?.disputed
          ? 'En Revisión'
          : c?.refunded
            ? 'Reintegrado'
            : c?.paid
              ? 'Pagado'
              : c?.status == 'failed'
                ? 'Fallida'
                : 'Pendiente',
        Hiperenlace: toTrimmedString(c?.receipt_url),
      }
    } else if (isFrench(selectedLanguage)) {
      ret = {
        Date: formatUnixTimestamp(c?.created, selectedLanguage, localCurrency),
        Référence: getLastSevenOfStripeId(c?.id),
        Méthode: getPaymentMethodDisplay(
          c?.payment_method_details,
          selectedLanguage,
        ),
        Somme: formatStripeFundsAmount(
          c?.amount,
          isNotNullOrEmptyString(c?.currency)
            ? toTrimmedString(c?.currency)
            : chargeCurrency,
          selectedLanguage,
        ),
        Statut: c?.disputed
          ? 'En Revue'
          : c?.refunded
            ? 'Remboursé'
            : c?.paid
              ? 'Payé'
              : c?.status == 'failed'
                ? 'Échoué'
                : 'En Attente',
        Lien: toTrimmedString(c?.receipt_url),
      }
    } else
      ret = {
        Date: formatUnixTimestamp(c?.created, selectedLanguage, localCurrency),
        Reference: getLastSevenOfStripeId(c?.id),
        Method: getPaymentMethodDisplay(
          c?.payment_method_details,
          selectedLanguage,
        ),
        Amount: formatStripeFundsAmount(
          c?.amount,
          isNotNullOrEmptyString(c?.currency)
            ? toTrimmedString(c?.currency)
            : chargeCurrency,
          selectedLanguage,
        ),
        Status: c?.disputed
          ? 'Review'
          : c?.refunded
            ? 'Refunded'
            : c?.paid
              ? 'Paid'
              : c?.status == 'failed'
                ? 'Failed'
                : 'Pending',
        Link: toTrimmedString(c?.receipt_url),
      }
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
  }
  return ret
}

export async function getInvitations(adminWeb2UserId: any) {
  return new Promise<any>(async (resolve) => {
    try {
      if (isNotNullOrEmptyString(adminWeb2UserId)) {
        const opts: any = {
          method: 'POST',
          body: JSON.stringify({
            adminWeb2UserId: toTrimmedString(adminWeb2UserId),
          }),
        }
        await fetch(HOST + '/api/p.stream-sv/i/gis', opts)
          .then(async (x: any) => {
            const ret: any = await x.json()
            resolve(ret?.invitations)
          })
          .catch((e: any) => {
            if (DEBUG_MODE) console.log(e)
            resolve([])
          })
      } else resolve([])
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve([])
    }
  })
}

export async function getInvitation(invitationToken: any) {
  return new Promise<any>(async (resolve) => {
    try {
      if (isNotNullOrEmptyString(invitationToken)) {
        const opts: any = {
          method: 'POST',
          body: JSON.stringify({
            invitationToken: toTrimmedString(invitationToken),
          }),
        }
        await fetch(HOST + '/api/p.stream-sv/i/gi', opts)
          .then(async (x: any) => {
            const ret: any = await x.json()
            resolve(ret?.invitation)
          })
          .catch((e: any) => {
            if (DEBUG_MODE) console.log(e)
            resolve(null)
          })
      } else resolve(null)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve(null)
    }
  })
}

export async function resendSingleInvitation(
  invitationToken: any,
  email: any,
  adminWeb2UserId: any,
) {
  return new Promise<any>(async (resolve) => {
    try {
      if (
        isNotNullOrEmptyString(invitationToken) &&
        isNotNullOrEmptyString(email) &&
        isNotNullOrEmptyString(adminWeb2UserId)
      ) {
        const opts: any = {
          method: 'POST',
          body: JSON.stringify({
            adminWeb2UserId: toTrimmedString(adminWeb2UserId),
            invitationToken: toTrimmedString(invitationToken),
            inviteEmail: toTrimmedString(email),
          }),
        }
        await fetch(HOST + '/api/p.stream-sv/i/rs', opts)
          .then(async (x: any) => {
            const ret: any = await x.json()
            resolve({ success: ret?.success, errorCode: ret?.errorCode })
          })
          .catch((e: any) => {
            if (DEBUG_MODE) console.log(e)
            resolve({
              success: false,
              errorCode: RESEND_SINGLE_INVITATION_ERROR_CODE_EXCEPTION,
            })
          })
      } else
        resolve({
          success: false,
          RESEND_SINGLE_INVITATION_ERROR_CODE_EXCEPTION,
        })
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve({
        success: false,
        errorCode: RESEND_SINGLE_INVITATION_ERROR_CODE_EXCEPTION,
      })
    }
  })
}

export async function saveInvitation(
  adminWeb2UserId: any,
  invitationToken: any,
  status: any,
  acceptedOn: any,
  acceptedByWeb2UserId: any,
  invitedEmail: any,
  businessUserGroupId: number | null = null,
) {
  return new Promise<any>(async (resolve) => {
    try {
      if (isNotNullOrEmptyString(invitationToken)) {
        const strBodyInvitationData: any = JSON.stringify({
          adminWeb2UserId: toStringOrNull(adminWeb2UserId),
          invitationToken: toTrimmedString(invitationToken),
          status: toStringOrNull(status),
          acceptedOn: toStringOrNull(acceptedOn),
          acceptedByWeb2UserId: toStringOrNull(acceptedByWeb2UserId),
          email: toStringOrNull(invitedEmail),
          businessUserGroupId: businessUserGroupId,
        })
        const urlencoded: URLSearchParams = new URLSearchParams({
          invitationData: JSON.stringify(JSON.parse(strBodyInvitationData)),
        })
        const opts: any = {
          method: 'POST',
          body: urlencoded,
        }
        await fetch(HOST + '/api/p.stream-sv/i/pi', opts)
          .then(async (resp: any) => {
            const respon: any = await resp.json()
            resolve({
              message: '',
              success: respon?.success,
              errorCode: respon?.errorCode,
              errorText: respon?.errorText,
              notificationInfo: respon?.notificationInfo,
            })
          })
          .catch((er: any) => {
            if (DEBUG_MODE) console.log(er)
            resolve({
              message: '',
              success: false,
              errorCode: -1,
              errorText: '',
              notificationInfo: null,
            })
          })
      } else
        resolve({
          message: '',
          success: false,
          errorCode: -1,
          errorText: '',
          notificationInfo: null,
        })
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve({
        message: '',
        success: false,
        errorCode: -1,
        errorText: '',
        notificationInfo: null,
      })
    }
  })
}

export async function sendBulkInvitations(
  adminWeb2UserId: any,
  invitedEmails: string[],
  businessUserGroupId: number | null = null,
) {
  return new Promise<any>(async (resolve) => {
    try {
      if (
        invitedEmails?.length > 0 &&
        isNotNullOrEmptyString(adminWeb2UserId)
      ) {
        const strBodyInvitationData: any = JSON.stringify({
          adminWeb2UserId: toStringOrNull(adminWeb2UserId),
          invitedEmails: invitedEmails,
          businessUserGroupId: businessUserGroupId,
        })
        const urlencoded: URLSearchParams = new URLSearchParams({
          invitationData: JSON.stringify(JSON.parse(strBodyInvitationData)),
        })
        const opts: any = {
          method: 'POST',
          body: urlencoded,
        }
        await fetch(HOST + '/api/p.stream-sv/i/sbi', opts)
          .then(async (resp: any) => {
            const respon: any = await resp.json()
            resolve({
              message: '',
              success: respon?.success,
              report: respon?.report,
            })
          })
          .catch((er: any) => {
            if (DEBUG_MODE) console.log(er)
            resolve({
              message: '',
              success: false,
              errorCode: -1,
              errorText: '',
              notificationInfo: null,
            })
          })
      } else
        resolve({
          message: '',
          success: false,
          report: null,
        })
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve({
        message: '',
        success: false,
        report: null,
      })
    }
  })
}

export async function createBulkInvitations(
  adminWeb2UserId: any,
  invitedEmails: string[],
  businessUserGroupId: number | null = null,
) {
  return new Promise<any>(async (resolve) => {
    try {
      if (
        invitedEmails?.length > 0 &&
        isNotNullOrEmptyString(adminWeb2UserId)
      ) {
        const strBodyInvitationData: any = JSON.stringify({
          adminWeb2UserId: toStringOrNull(adminWeb2UserId),
          invitedEmails: invitedEmails,
          businessUserGroupId: businessUserGroupId,
        })
        const urlencoded: URLSearchParams = new URLSearchParams({
          invitationData: JSON.stringify(JSON.parse(strBodyInvitationData)),
        })
        const opts: any = {
          method: 'POST',
          body: urlencoded,
        }
        await fetch(HOST + '/api/p.stream-sv/i/cbi', opts)
          .then(async (resp: any) => {
            const respon: any = await resp.json()
            resolve({
              message: '',
              success: respon?.success,
              report: respon?.report,
            })
          })
          .catch((er: any) => {
            if (DEBUG_MODE) console.log(er)
            resolve({
              message: '',
              success: false,
              report: null,
            })
          })
      } else
        resolve({
          message: '',
          success: false,
          report: null,
        })
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve({
        message: '',
        success: false,
        report: null,
      })
    }
  })
}

export async function undeclineInvitation(invitationToken: any) {
  return new Promise<boolean>(async (resolve) => {
    try {
      if (isNotNullOrEmptyString(invitationToken)) {
        const strBodyInvitationData: any = JSON.stringify({
          invitationToken: toTrimmedString(invitationToken),
        })
        const urlencoded: URLSearchParams = new URLSearchParams({
          invitationData: JSON.stringify(JSON.parse(strBodyInvitationData)),
        })
        const opts: any = {
          method: 'POST',
          body: urlencoded,
        }
        await fetch(HOST + '/api/p.stream-sv/i/ud', opts)
          .then(async (resp: any) => {
            const respon: any = await resp.json()
            resolve(respon?.success == true)
          })
          .catch((er: any) => {
            if (DEBUG_MODE) console.log(er)
            resolve(false)
          })
      } else resolve(false)
    } catch (e) {
      if (DEBUG_MODE) console.log(e)
      resolve(false)
    }
  })
}

export function isActiveStripeSubscription(
  stripeStatus: any,
  stripeCancelAt: any,
) {
  return (
    isNullOrEmptyString(stripeCancelAt) &&
    (toTrimmedStringLowerCase(stripeStatus) ==
      STRIPE_SUBSCRIPTION_STATUS_ACTIVE ||
      toTrimmedStringLowerCase(stripeStatus) ==
        STRIPE_SUBSCRIPTION_STATUS_TRIALING ||
      toTrimmedStringLowerCase(stripeStatus) ==
        STRIPE_SUBSCRIPTION_STATUS_PAUSED ||
      toTrimmedStringLowerCase(stripeStatus) ==
        STRIPE_SUBSCRIPTION_STATUS_PAST_DUE)
  )
}

export function canStripeSubscriptionBeReactivated(
  stripeStatus: any,
  stripeCancelAtPeriodEnd: any,
) {
  return (
    stripeStatus == STRIPE_SUBSCRIPTION_STATUS_ACTIVE &&
    stripeCancelAtPeriodEnd == true
  )
}

export const IMG_SRC_VISA: string =
  '/images/18961EB1-BCBD-44E5-A9FC-76E315B0B003.jpg'
export const IMG_SRC_VISA_AVIF: string =
  '/images/18961EB1-BCBD-44E5-A9FC-76E315B0B003.avif'
export const IMG_SRC_MASTERCARD: string =
  '/images/3EA062C8-251D-4C4C-84F4-A79D665C38A3.png'
export const IMG_SRC_MASTERCARD_AVIF: string =
  '/images/3EA062C8-251D-4C4C-84F4-A79D665C38A3.avif'
export const IMG_SRC_DISCOVER: string =
  '/images/E06549F3-186F-4B30-8A14-CF228AD2D417.jpg'
export const IMG_SRC_DISCOVER_AVIF: string =
  '/images/E06549F3-186F-4B30-8A14-CF228AD2D417.avif'
export const IMG_SRC_AMERICAN_EXPRESS: string =
  '/images/2FB7E1BC-D7B4-49F1-99EF-4768A2C94149.png'
export const IMG_SRC_AMERICAN_EXPRESS_AVIF: string =
  '/images/2FB7E1BC-D7B4-49F1-99EF-4768A2C94149.avif'
export const IMG_SRC_DINERS: string =
  '/images/6F71B10D-DA10-44CF-8A76-B78DCDA7FAFE.png'
export const IMG_SRC_DINERS_AVIF: string =
  '/images/6F71B10D-DA10-44CF-8A76-B78DCDA7FAFE.avif'
export const IMG_SRC_JCB: string =
  '/images/81216197-445B-464C-9A84-A378019E7B5B.png'
export const IMG_SRC_JCB_AVIF: string =
  '/images/81216197-445B-464C-9A84-A378019E7B5B.avif'
export const IMG_SRC_EFTPOS_AU: string =
  '/images/D66D4F9D-DD01-41FB-9E71-87F536A6F107.png'
export const IMG_SRC_EFTPOS_AU_AVIF: string =
  '/images/D66D4F9D-DD01-41FB-9E71-87F536A6F107.avif'
export const IMG_SRC_UNIONPAY: string =
  '/images/826D04E5-404C-4471-82FD-3DE88B5B9BA0.png'
export const IMG_SRC_UNIONPAY_AVIF: string =
  '/images/826D04E5-404C-4471-82FD-3DE88B5B9BA0.avif'
export const IMG_SRC_GENERIC_CARD: string =
  '/images/F27E628D-85B6-420D-B294-09E54C349A9B.png'
export const IMG_SRC_GENERIC_CARD_AVIF: string =
  '/images/F27E628D-85B6-420D-B294-09E54C349A9B.avif'
export const IMG_SRC_STRIPE_LINK: string =
  '/images/CF8C7608-703D-4185-BC53-D822BE19366C.png'
export const IMG_SRC_STRIPE_LINK_AVIF: string =
  '/images/CF8C7608-703D-4185-BC53-D822BE19366C.avif'

export const USER_SCHEDULED_FOR_DELETION_SUBSCRIBED_STR: string =
  '1/1/2100 12:00:00 AM'
export const USER_SCHEDULED_FOR_DELETION_SUBSCRIBED_DATE: Date = new Date(
  '2100-01-01',
)

export const USER_SCHEDULED_FOR_DELETION_YEAR: number = 2100
export const USER_SCHEDULED_FOR_DELETION_YEAR_LESS_ONE: number =
  USER_SCHEDULED_FOR_DELETION_YEAR - 1

export function isDeactivationScheduledYear(subscribed: any) {
  let ret: boolean = false
  try {
    const year: number = new Date(subscribed)?.getFullYear()
    ret =
      year === USER_SCHEDULED_FOR_DELETION_YEAR_LESS_ONE ||
      year === USER_SCHEDULED_FOR_DELETION_YEAR
  } catch (e) {
    if (DEBUG_MODE) console.log(e)
  }
  return ret
}

export const STATUS_AUTHENTICATED: string = 'authenticated'

export const MINIMUM_SHOW_MAX_SUBSCRIPTION_ROWS: number = 10
export const SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_1: number = 25
export const SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_2: number = 50
export const MAXIMUM_SHOW_MAX_SUBSCRIPTION_ROWS: number = 100

export const MINIMUM_SHOW_MAX_SUBSCRIPTION_ROWS_MOBILE: number = 10
export const SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_1_MOBILE: number = 25
export const SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_2_MOBILE: number = 40
export const MAXIMUM_SHOW_MAX_SUBSCRIPTION_ROWS_MOBILE: number = 50

export function showMaxSubscriptionOptions(
  selectedLanguage: any,
  isMobileOnly: any,
) {
  return [
    {
      label: translate(
        'Show ' +
          (!isMobileOnly
            ? toTrimmedString(MINIMUM_SHOW_MAX_SUBSCRIPTION_ROWS)
            : toTrimmedString(MINIMUM_SHOW_MAX_SUBSCRIPTION_ROWS_MOBILE)),
        'Mostrar ' +
          (!isMobileOnly
            ? toTrimmedString(MINIMUM_SHOW_MAX_SUBSCRIPTION_ROWS)
            : toTrimmedString(MINIMUM_SHOW_MAX_SUBSCRIPTION_ROWS_MOBILE)),
        'Afficher ' +
          (!isMobileOnly
            ? toTrimmedString(MINIMUM_SHOW_MAX_SUBSCRIPTION_ROWS)
            : toTrimmedString(MINIMUM_SHOW_MAX_SUBSCRIPTION_ROWS_MOBILE)),
        '쇼 ' +
          (!isMobileOnly
            ? toTrimmedString(MINIMUM_SHOW_MAX_SUBSCRIPTION_ROWS)
            : toTrimmedString(MINIMUM_SHOW_MAX_SUBSCRIPTION_ROWS_MOBILE)),
        selectedLanguage,
      ),
      value: !isMobileOnly
        ? MINIMUM_SHOW_MAX_SUBSCRIPTION_ROWS
        : MINIMUM_SHOW_MAX_SUBSCRIPTION_ROWS_MOBILE,
    },
    {
      label: translate(
        'Show ' +
          (!isMobileOnly
            ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_1)
            : toTrimmedString(
                SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_1_MOBILE,
              )),
        'Mostrar ' +
          (!isMobileOnly
            ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_1)
            : toTrimmedString(
                SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_1_MOBILE,
              )),
        'Afficher ' +
          (!isMobileOnly
            ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_1)
            : toTrimmedString(
                SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_1_MOBILE,
              )),
        '쇼 ' +
          (!isMobileOnly
            ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_1)
            : toTrimmedString(
                SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_1_MOBILE,
              )),
        selectedLanguage,
      ),
      value: !isMobileOnly
        ? SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_1
        : SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_1_MOBILE,
    },
    {
      label: translate(
        'Show ' +
          (!isMobileOnly
            ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_2)
            : toTrimmedString(
                SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_2_MOBILE,
              )),
        'Mostrar ' +
          (!isMobileOnly
            ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_2)
            : toTrimmedString(
                SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_2_MOBILE,
              )),
        'Afficher ' +
          (!isMobileOnly
            ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_2)
            : toTrimmedString(
                SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_2_MOBILE,
              )),
        '쇼 ' +
          (!isMobileOnly
            ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_2)
            : toTrimmedString(
                SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_2_MOBILE,
              )),
        selectedLanguage,
      ),
      value: !isMobileOnly
        ? SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_2
        : SHOW_MORE_THAN_MINIMUM_SUBSCRIPTION_ROWS_2_MOBILE,
    },
    {
      label: translate(
        'Show ' +
          (!isMobileOnly
            ? toTrimmedString(MAXIMUM_SHOW_MAX_SUBSCRIPTION_ROWS)
            : toTrimmedString(MAXIMUM_SHOW_MAX_SUBSCRIPTION_ROWS_MOBILE)),
        'Mostrar ' +
          (!isMobileOnly
            ? toTrimmedString(MAXIMUM_SHOW_MAX_SUBSCRIPTION_ROWS)
            : toTrimmedString(MAXIMUM_SHOW_MAX_SUBSCRIPTION_ROWS_MOBILE)),
        'Afficher ' +
          (!isMobileOnly
            ? toTrimmedString(MAXIMUM_SHOW_MAX_SUBSCRIPTION_ROWS)
            : toTrimmedString(MAXIMUM_SHOW_MAX_SUBSCRIPTION_ROWS_MOBILE)),
        '쇼 ' +
          (!isMobileOnly
            ? toTrimmedString(MAXIMUM_SHOW_MAX_SUBSCRIPTION_ROWS)
            : toTrimmedString(MAXIMUM_SHOW_MAX_SUBSCRIPTION_ROWS_MOBILE)),
        selectedLanguage,
      ),
      value: !isMobileOnly
        ? MAXIMUM_SHOW_MAX_SUBSCRIPTION_ROWS
        : MAXIMUM_SHOW_MAX_SUBSCRIPTION_ROWS_MOBILE,
    },
  ]
}
export const MINIMUM_SHOW_MAX_CHARGES_ROWS: number = 10
export const SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_1: number = 25
export const SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_2: number = 50
export const MAXIMUM_SHOW_MAX_CHARGES_ROWS: number = 100

export const MINIMUM_SHOW_MAX_CHARGES_MOBILE: number = 10
export const SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_1_MOBILE: number = 25
export const SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_2_MOBILE: number = 40
export const MAXIMUM_SHOW_MAX_CHARGES_ROWS_MOBILE: number = 50

export const MINIMUM_SHOW_MAX_NOTIFICATIONS_ROWS: number = 10
export const SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_1: number = 25
export const SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_2: number = 40
export const MAXIMUM_SHOW_MAX_NOTIFICATIONS_ROWS: number = 50

export const MINIMUM_SHOW_MAX_NOTIFICATIONS_MOBILE: number = 10
export const SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_1_MOBILE: number = 25
export const SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_2_MOBILE: number = 40
export const MAXIMUM_SHOW_MAX_NOTIFICATIONS_ROWS_MOBILE: number = 50

export const MAXIMUM_SHOW_MAX_NOTIFICATIONS_ROWS_ALL: number =
  MAXIMUM_SHOW_MAX_NOTIFICATIONS_ROWS_MOBILE + 1

export const EMAIL_TYPE_ALL_EMAILS: number = 1

export function showMaxChargesOptions(
  selectedLanguage: any,
  isMobileOnly: any,
) {
  return [
    {
      label: translate(
        'Show ' +
          (!isMobileOnly
            ? toTrimmedString(MINIMUM_SHOW_MAX_CHARGES_ROWS)
            : toTrimmedString(MINIMUM_SHOW_MAX_CHARGES_MOBILE)),
        'Mostrar ' +
          (!isMobileOnly
            ? toTrimmedString(MINIMUM_SHOW_MAX_CHARGES_ROWS)
            : toTrimmedString(MINIMUM_SHOW_MAX_CHARGES_MOBILE)),
        'Afficher ' +
          (!isMobileOnly
            ? toTrimmedString(MINIMUM_SHOW_MAX_CHARGES_ROWS)
            : toTrimmedString(MINIMUM_SHOW_MAX_CHARGES_MOBILE)),
        '쇼 ' +
          (!isMobileOnly
            ? toTrimmedString(MINIMUM_SHOW_MAX_CHARGES_ROWS)
            : toTrimmedString(MINIMUM_SHOW_MAX_CHARGES_MOBILE)),
        selectedLanguage,
      ),
      value: !isMobileOnly
        ? MINIMUM_SHOW_MAX_CHARGES_ROWS
        : MINIMUM_SHOW_MAX_CHARGES_MOBILE,
    },
    {
      label: translate(
        'Show ' +
          (!isMobileOnly
            ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_1)
            : toTrimmedString(SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_1_MOBILE)),
        'Mostrar ' +
          (!isMobileOnly
            ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_1)
            : toTrimmedString(SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_1_MOBILE)),
        'Afficher ' +
          (!isMobileOnly
            ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_1)
            : toTrimmedString(SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_1_MOBILE)),
        '쇼 ' +
          (!isMobileOnly
            ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_1)
            : toTrimmedString(SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_1_MOBILE)),
        selectedLanguage,
      ),
      value: !isMobileOnly
        ? SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_1
        : SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_1_MOBILE,
    },
    {
      label: translate(
        'Show ' +
          (!isMobileOnly
            ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_2)
            : toTrimmedString(SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_2_MOBILE)),
        'Mostrar ' +
          (!isMobileOnly
            ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_2)
            : toTrimmedString(SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_2_MOBILE)),
        'Afficher ' +
          (!isMobileOnly
            ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_2)
            : toTrimmedString(SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_2_MOBILE)),
        '쇼 ' +
          (!isMobileOnly
            ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_2)
            : toTrimmedString(SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_2_MOBILE)),
        selectedLanguage,
      ),
      value: !isMobileOnly
        ? SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_2
        : SHOW_MORE_THAN_MINIMUM_CHARGES_ROWS_2_MOBILE,
    },
    {
      label: translate(
        'Show ' +
          (!isMobileOnly
            ? toTrimmedString(MAXIMUM_SHOW_MAX_CHARGES_ROWS)
            : toTrimmedString(MAXIMUM_SHOW_MAX_CHARGES_ROWS_MOBILE)),
        'Mostrar ' +
          (!isMobileOnly
            ? toTrimmedString(MAXIMUM_SHOW_MAX_CHARGES_ROWS)
            : toTrimmedString(MAXIMUM_SHOW_MAX_CHARGES_ROWS_MOBILE)),
        'Afficher ' +
          (!isMobileOnly
            ? toTrimmedString(MAXIMUM_SHOW_MAX_CHARGES_ROWS)
            : toTrimmedString(MAXIMUM_SHOW_MAX_CHARGES_ROWS_MOBILE)),
        '쇼 ' +
          (!isMobileOnly
            ? toTrimmedString(MAXIMUM_SHOW_MAX_CHARGES_ROWS)
            : toTrimmedString(MAXIMUM_SHOW_MAX_CHARGES_ROWS_MOBILE)),
        selectedLanguage,
      ),
      value: !isMobileOnly
        ? MAXIMUM_SHOW_MAX_CHARGES_ROWS
        : MAXIMUM_SHOW_MAX_CHARGES_ROWS_MOBILE,
    },
  ]
}

export function showMaxNotificationsOptions(
  selectedLanguage: any,
  isMobileOnly: any,
  maxNow: any,
) {
  let ret: any[] = []
  const showAll: any = {
    label: translate(
      'Show All',
      'Mostrar Todo',
      'Afficher Tout',
      '모두 표시',
      selectedLanguage,
    ),
    value: MAXIMUM_SHOW_MAX_NOTIFICATIONS_ROWS_ALL,
  }
  ret.push({
    label: translate(
      'Show ' +
        (!isMobileOnly
          ? toTrimmedString(MINIMUM_SHOW_MAX_NOTIFICATIONS_ROWS)
          : toTrimmedString(MINIMUM_SHOW_MAX_NOTIFICATIONS_MOBILE)),
      'Mostrar ' +
        (!isMobileOnly
          ? toTrimmedString(MINIMUM_SHOW_MAX_NOTIFICATIONS_ROWS)
          : toTrimmedString(MINIMUM_SHOW_MAX_NOTIFICATIONS_MOBILE)),
      'Afficher ' +
        (!isMobileOnly
          ? toTrimmedString(MINIMUM_SHOW_MAX_NOTIFICATIONS_ROWS)
          : toTrimmedString(MINIMUM_SHOW_MAX_NOTIFICATIONS_MOBILE)),
      '쇼 ' +
        (!isMobileOnly
          ? toTrimmedString(MINIMUM_SHOW_MAX_NOTIFICATIONS_ROWS)
          : toTrimmedString(MINIMUM_SHOW_MAX_NOTIFICATIONS_MOBILE)),
      selectedLanguage,
    ),
    value: !isMobileOnly
      ? MINIMUM_SHOW_MAX_NOTIFICATIONS_ROWS
      : MINIMUM_SHOW_MAX_NOTIFICATIONS_MOBILE,
  })
  if (
    maxNow <
    (!isMobileOnly
      ? SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_1
      : SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_1_MOBILE)
  )
    ret.push(showAll)
  else {
    ret.push({
      label: translate(
        'Show ' +
          (!isMobileOnly
            ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_1)
            : toTrimmedString(
                SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_1_MOBILE,
              )),
        'Mostrar ' +
          (!isMobileOnly
            ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_1)
            : toTrimmedString(
                SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_1_MOBILE,
              )),
        'Afficher ' +
          (!isMobileOnly
            ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_1)
            : toTrimmedString(
                SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_1_MOBILE,
              )),
        '쇼 ' +
          (!isMobileOnly
            ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_1)
            : toTrimmedString(
                SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_1_MOBILE,
              )),
        selectedLanguage,
      ),
      value: !isMobileOnly
        ? SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_1
        : SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_1_MOBILE,
    })
    if (
      maxNow <
      (!isMobileOnly
        ? SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_2
        : SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_2_MOBILE)
    ) {
      ret.push(showAll)
    } else {
      ret.push({
        label: translate(
          'Show ' +
            (!isMobileOnly
              ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_2)
              : toTrimmedString(
                  SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_2_MOBILE,
                )),
          'Mostrar ' +
            (!isMobileOnly
              ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_2)
              : toTrimmedString(
                  SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_2_MOBILE,
                )),
          'Afficher ' +
            (!isMobileOnly
              ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_2)
              : toTrimmedString(
                  SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_2_MOBILE,
                )),
          '쇼 ' +
            (!isMobileOnly
              ? toTrimmedString(SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_2)
              : toTrimmedString(
                  SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_2_MOBILE,
                )),
          selectedLanguage,
        ),
        value: !isMobileOnly
          ? SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_2
          : SHOW_MORE_THAN_MINIMUM_NOTIFICATIONS_ROWS_2_MOBILE,
      })
      if (
        maxNow <
        (!isMobileOnly
          ? MAXIMUM_SHOW_MAX_NOTIFICATIONS_ROWS
          : MAXIMUM_SHOW_MAX_NOTIFICATIONS_ROWS_MOBILE)
      ) {
        ret.push(showAll)
      } else
        ret.push({
          label: translate(
            'Show ' +
              (!isMobileOnly
                ? toTrimmedString(MAXIMUM_SHOW_MAX_NOTIFICATIONS_ROWS)
                : toTrimmedString(MAXIMUM_SHOW_MAX_NOTIFICATIONS_ROWS_MOBILE)),
            'Mostrar ' +
              (!isMobileOnly
                ? toTrimmedString(MAXIMUM_SHOW_MAX_NOTIFICATIONS_ROWS)
                : toTrimmedString(MAXIMUM_SHOW_MAX_NOTIFICATIONS_ROWS_MOBILE)),
            'Afficher ' +
              (!isMobileOnly
                ? toTrimmedString(MAXIMUM_SHOW_MAX_NOTIFICATIONS_ROWS)
                : toTrimmedString(MAXIMUM_SHOW_MAX_NOTIFICATIONS_ROWS_MOBILE)),
            '쇼 ' +
              (!isMobileOnly
                ? toTrimmedString(MAXIMUM_SHOW_MAX_NOTIFICATIONS_ROWS)
                : toTrimmedString(MAXIMUM_SHOW_MAX_NOTIFICATIONS_ROWS_MOBILE)),
            selectedLanguage,
          ),
          value: !isMobileOnly
            ? MAXIMUM_SHOW_MAX_NOTIFICATIONS_ROWS
            : MAXIMUM_SHOW_MAX_NOTIFICATIONS_ROWS_MOBILE,
        })
    }
  }
  return ret
}
